<script setup lang="ts">
import CornerLogo from '@/components/CornerLogo.vue';
import ExternalLink from '@/components/ExternalLink.vue';
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import RouterLink from '@/components/RouterLink.vue';
import { useSeoMeta } from '@unhead/vue';
import { Head } from 'vike-vue/Head';

useSeoMeta({
  title: 'Policies - Parsec Skateboards',
});
</script>

<template>
  <Head>
    <title>Policies - Parsec Skateboards</title>
    <meta name="description" content="Policies" />
  </Head>

  <main>
    <Header />

    <section class="relative bg-primary md:pt-20 pb-12 col-span-12 rounded-lg flex flex-col items-center">
      <CornerLogo class="hidden md:block" />

      <div class="max-w-4xl w-full text-content">
        <h1 v-anchor>Return & Refunds</h1>

        <p>
          Our return and refund policy applies to products purchased from this website only (<a
            href="https://rideparsec.com"
            >RideParsec.com</a
          >). For Parsec products purchased elsewhere, refer to the policy of where the product was purchased.
        </p>

        <p>
          <strong class="text-red-500">At this time</strong>, all orders delivered outside of North America are final
          and returns are <strong class="text-red-500">NOT</strong> accepted.
        </p>

        <p>
          For orders delivered inside North America, we accept returns for both new and lightly used items (except
          consumables) within 14 days of the order's delivery date. Returned items must include all factory packaging
          and accessories. Customer is responsible for return shipping fees.
        </p>

        <h4>The following will be deducted from the refund:</h4>
        <ul>
          <li>Original shipping fees and customs duties</li>
          <li>Payment processor fees (up to 3%)</li>
          <li>Deductions for components showing signs of use</li>
          <li>Deductions for anything missing</li>
        </ul>

        <h2>Certain items are consumables and not refundable if used:</h2>
        <ul>
          <li>Griptape</li>
          <li>Wheels</li>
          <li>Bearings</li>
          <li>Belts</li>
        </ul>

        <p>
          The final refund amount will be determined by our team following a thorough inspection of the returned item.
          Refunds cannot be issued for items deemed unsuitable for refurbishment and resale by our standards.
        </p>

        <!-- todo: that -->
        <p>To return an item, fill out this return form and follow its instructions: <a href="#">[link]</a></p>

        <p>
          Once your return is processed, you will be notified by email and your refund will be issued to your original
          payment method.
        </p>
      </div>
    </section>

    <section class="bg-primary pb-12 col-span-12 rounded-lg flex flex-col items-center">
      <div class="max-w-4xl w-full text-content">
        <h1 v-anchor>Privacy Policy</h1>

        <h3 class="my-4">Last updated: March 13, 2024</h3>

        <p>
          This Privacy Policy describes how Parsec (the "<strong>Site</strong>", "<strong>we</strong>",
          "<strong>us</strong>", or "<strong>our</strong>") collects, uses, and discloses your personal information when
          you visit, use our services, or make a purchase from
          <ExternalLink href="https://rideparsec.com">RideParsec.com</ExternalLink> (the "<strong>Site</strong>") or
          otherwise communicate with us (collectively, the "<strong>Services</strong>"). For purposes of this Privacy
          Policy, "<strong>you</strong>" and "<strong>your</strong>" means you as the user of the Services, whether you
          are a customer, website visitor, or another individual whose information we have collected pursuant to this
          Privacy Policy.
        </p>

        <p>
          Please read this Privacy Policy carefully. By using and accessing any of the Services, you agree to the
          collection, use, and disclosure of your information as described in this Privacy Policy. If you do not agree
          to this Privacy Policy, please do not use or access any of the Services.
        </p>

        <h2 v-anchor>Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time, including to reflect changes to our practices or for
          other operational, legal, or regulatory reasons. We will post the revised Privacy Policy on the Site, update
          the "Last updated" date and take any other steps required by applicable law.
        </p>

        <h2 v-anchor>How We Collect and Use Your Personal Information</h2>
        <p>
          To provide the Services, we collect and have collected over the past 12 months personal information about you
          from a variety of sources, as set out below. The information that we collect and use varies depending on how
          you interact with us.
        </p>
        <p>
          In addition to the specific uses set out below, we may use information we collect about you to communicate
          with you, provide the Services, comply with any applicable legal obligations, enforce any applicable terms of
          service, and to protect or defend the Services, our rights, and the rights of our users or others.
        </p>

        <h2 v-anchor>What Personal Information We Collect</h2>
        <p>
          The types of personal information we obtain about you depends on how you interact with our Site and use our
          Services. When we use the term "personal information", we are referring to information that identifies,
          relates to, describes or can be associated with you. The following sections describe the categories and
          specific types of personal information we collect.
        </p>

        <h2 v-anchor>Information We Collect Directly from You</h2>
        <h4>Information that you directly submit to us through our Services may include:</h4>

        <ul>
          <li><strong>Basic contact details</strong> including your name, address, phone number, email.</li>
          <li>
            <strong>Order information</strong> including your name, billing address, shipping address, payment
            confirmation, email address, phone number.
          </li>
          <li><strong>Account information</strong> including your username, password, security questions.</li>
          <li>
            <strong>Shopping information</strong> including the items you view, put in your cart or add to your
            wishlist.
          </li>
          <li>
            <strong>Customer support information</strong> including the information you choose to include in
            communications with us, for example, when sending a message through the Services.
          </li>
        </ul>
        <p>
          Some features of the Services may require you to directly provide us with certain information about yourself.
          You may elect not to provide this information, but doing so may prevent you from using or accessing these
          features.
        </p>

        <h2 v-anchor>Information We Collect through Cookies</h2>
        <p>
          We also automatically collect certain information about your interaction with the Services ("<strong
            >Usage Data</strong
          >"). To do this, we may use cookies, pixels and similar technologies ("<strong>Cookies</strong>"). Usage Data
          may include information about how you access and use our Site and your account, including device information,
          browser information, information about your network connection, your IP address and other information
          regarding your interaction with the Services.
        </p>

        <h2 v-anchor>Information We Obtain from Third Parties</h2>
        <h4>
          Finally, we may obtain information about you from third parties, including from vendors and service providers
          who may collect information on our behalf, such as:
        </h4>
        <ul>
          <li>Companies who support our Site and Services, such as Shopify.</li>
          <li>
            Our payment processors, who collect payment information (e.g., bank account, credit or debit card
            information, billing address) to process your payment in order to fulfill your orders and provide you with
            products or services you have requested, in order to perform our contract with you.
          </li>
          <li>
            When you visit our Site, open or click on emails we send you, or interact with our Services or
            advertisements, we, or third parties we work with, may automatically collect certain information using
            online tracking technologies such as pixels, web beacons, software developer kits, third-party libraries,
            and cookies.
          </li>
        </ul>

        <p>
          Any information we obtain from third parties will be treated in accordance with this Privacy Policy. We are
          not responsible or liable for the accuracy of the information provided to us by third parties and are not
          responsible for any third party's policies or practices. For more information, see the section below,
          <RouterLink to="#third-party-websites-and-links">Third Party Websites and Links</RouterLink>.
        </p>

        <h2 v-anchor>How We Use Your Personal Information</h2>
        <ul>
          <li>
            <strong>Providing Products and Services.</strong> We use your personal information to provide you with the
            Services in order to perform our contract with you, including to process your payments, fulfill your orders,
            to send notifications to you related to you account, purchases, returns, exchanges or other transactions, to
            create, maintain and otherwise manage your account, to arrange for shipping, facilitate any returns and
            exchanges and to enable you to post reviews.
          </li>
          <li>
            <strong>Marketing and Advertising.</strong> We use your personal information for marketing and promotional
            purposes, such as to send marketing, advertising and promotional communications by email, text message or
            postal mail, and to show you advertisements for products or services. This may include using your personal
            information to better tailor the Services and advertising on our Site and other websites.
          </li>
          <li>
            <strong>Security and Fraud Prevention.</strong> We use your personal information to detect, investigate or
            take action regarding possible fraudulent, illegal or malicious activity. If you choose to use the Services
            and register an account, you are responsible for keeping your account credentials safe. We highly recommend
            that you do not share your username, password, or other access details with anyone else. If you believe your
            account has been compromised, please contact us immediately.
          </li>
          <li>
            <strong>Communicating with you.</strong> We use your personal information to provide you with customer
            support and improve our Services. This is in our legitimate interests in order to be responsive to you, to
            provide effective services to you, and to maintain our business relationship with you.
          </li>
        </ul>

        <h2 v-anchor>Cookies</h2>
        <p>
          Like many websites, we use Cookies on our Site. For specific information about the Cookies that we use related
          to powering our store with Shopify, see
          <ExternalLink href="https://www.shopify.com/legal/cookies">https://www.shopify.com/legal/cookies</ExternalLink
          >. We use Cookies to power and improve our Site and our Services (including to remember your actions and
          preferences), to run analytics and better understand user interaction with the Services (in our legitimate
          interests to administer, improve and optimize the Services). We may also permit third parties and services
          providers to use Cookies on our Site to better tailor the services, products and advertising on our Site and
          other websites.
        </p>

        <p>
          Most browsers automatically accept Cookies by default, but you can choose to set your browser to remove or
          reject Cookies through your browser controls. Please keep in mind that removing or blocking Cookies can
          negatively impact your user experience and may cause some of the Services, including certain features and
          general functionality, to work incorrectly or no longer be available. Additionally, blocking Cookies may not
          completely prevent how we share information with third parties such as our advertising partners.
        </p>

        <h2 v-anchor>How We Disclose Personal Information</h2>
        <h4>
          In certain circumstances, we may disclose your personal information to third parties for legitimate purposes
          subject to this Privacy Policy. Such circumstances may include:
        </h4>

        <ul>
          <li>
            With vendors or other third parties who perform services on our behalf (e.g., IT management, payment
            processing, data analytics, customer support, cloud storage, fulfillment and shipping).
          </li>
          <li>
            With business and marketing partners, including Shopify, to provide services and advertise to you. Our
            business and marketing partners will use your information in accordance with their own privacy notices.
          </li>
          <li>
            When you direct, request us or otherwise consent to our disclosure of certain information to third parties,
            such as to ship you products or through your use of social media widgets or login integrations, with your
            consent.
          </li>
          <li>
            With our affiliates or otherwise within our corporate group, in our legitimate interests to run a successful
            business.
          </li>
          <li>
            In connection with a business transaction such as a merger or bankruptcy, to comply with any applicable
            legal obligations (including to respond to subpoenas, search warrants and similar requests), to enforce any
            applicable terms of service, and to protect or defend the Services, our rights, and the rights of our users
            or others.
          </li>
        </ul>

        <p>
          We have, in the past 12 months disclosed the following categories of personal information and sensitive
          personal information (denoted by *) about users for the purposes set out above in
          <RouterLink to="#how-we-collect-and-use-your-personal-information">
            How we Collect and Use your Personal Information
          </RouterLink>
          and
          <RouterLink to="#how-we-disclose-personal-information">How we Disclose Personal Information</RouterLink>:
        </p>

        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Categories of Recipients</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ul>
                  <li>Identifiers such as basic contact details and certain order and account information</li>
                  <li>
                    Commercial information such as order information, shopping information and customer support
                    information
                  </li>
                  <li>Internet or other similar network activity, such as Usage Data</li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>
                    Vendors and third parties who perform services on our behalf (such as Internet service providers,
                    payment processors, fulfillment partners, customer support partners and data analytics providers)
                  </li>
                  <li>Business and marketing partners</li>
                  <li>Affiliates</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>

        <p>
          We do not use or disclose sensitive personal information for the purposes of inferring characteristics about
          you.
        </p>

        <h2 v-anchor>User Generated Content</h2>
        <p>
          The Services may enable you to post product reviews and other user-generated content. If you choose to submit
          user generated content to any public area of the Services, this content will be public and accessible by
          anyone.
        </p>
        <p>
          We do not control who will have access to the information that you choose to make available to others, and
          cannot ensure that parties who have access to such information will respect your privacy or keep it secure. We
          are not responsible for the privacy or security of any information that you make publicly available, or for
          the accuracy, use or misuse of any information that you disclose or receive from third parties.
        </p>

        <h2 v-anchor>Third Party Websites and Links</h2>
        <p>
          Our Site may provide links to websites or other online platforms operated by third parties. If you follow
          links to sites not affiliated or controlled by us, you should review their privacy and security policies and
          other terms and conditions. We do not guarantee and are not responsible for the privacy or security of such
          sites, including the accuracy, completeness, or reliability of information found on these sites. Information
          you provide on public or semi-public venues, including information you share on third-party social networking
          platforms may also be viewable by other users of the Services and/or users of those third-party platforms
          without limitation as to its use by us or by a third party. Our inclusion of such links does not, by itself,
          imply any endorsement of the content on such platforms or of their owners or operators, except as disclosed on
          the Services.
        </p>

        <h2 v-anchor>Children’s Data</h2>
        <p>
          The Services are not intended to be used by children, and we do not knowingly collect any personal information
          about children. If you are the parent or guardian of a child who has provided us with their personal
          information, you may contact us using the contact details set out below to request that it be deleted.
        </p>
        <p>
          As of the Effective Date of this Privacy Policy, we do not have actual knowledge that we “share” or “sell” (as
          those terms are defined in applicable law) personal information of individuals under 16 years of age.
        </p>

        <h2 v-anchor>Security and Retention of Your Information</h2>
        <p>
          Please be aware that no security measures are perfect or impenetrable, and we cannot guarantee “perfect
          security.” In addition, any information you send to us may not be secure while in transit. We recommend that
          you do not use unsecure channels to communicate sensitive or confidential information to us.
        </p>
        <p>
          How long we retain your personal information depends on different factors, such as whether we need the
          information to maintain your account, to provide the Services, comply with legal obligations, resolve disputes
          or enforce other applicable contracts and policies.
        </p>

        <h2 v-anchor>Your Rights and Choices</h2>
        <h4>
          Depending on where you live, you may have some or all of the rights listed below in relation to your personal
          information. However, these rights are not absolute, may apply only in certain circumstances and, in certain
          cases, we may decline your request as permitted by law.
        </h4>

        <ul>
          <li>
            <strong>Right to Access / Know.</strong> You may have a right to request access to personal information that
            we hold about you, including details relating to the ways in which we use and share your information.
          </li>
          <li>
            <strong>Right to Delete.</strong> You may have a right to request that we delete personal information we
            maintain about you.
          </li>
          <li>
            <strong>Right to Correct.</strong> You may have a right to request that we correct inaccurate personal
            information we maintain about you.
          </li>
          <li>
            <strong>Right of Portability.</strong> You may have a right to receive a copy of the personal information we
            hold about you and to request that we transfer it to a third party, in certain circumstances and with
            certain exceptions.
          </li>
          <li>
            <strong>Restriction of Processing:</strong> You may have the right to ask us to stop or restrict our
            processing of personal information.
          </li>
          <li>
            <strong>Withdrawal of Consent:</strong> Where we rely on consent to process your personal information, you
            may have the right to withdraw this consent.
          </li>
          <li>
            <strong>Appeal:</strong> You may have a right to appeal our decision if we decline to process your request.
            You can do so by replying directly to our denial.
          </li>
          <li>
            <strong>Managing Communication Preferences:</strong> We may send you promotional emails, and you may opt out
            of receiving these at any time by using the unsubscribe option displayed in our emails to you. If you opt
            out, we may still send you non-promotional emails, such as those about your account or orders that you have
            made.
          </li>
        </ul>

        <p>
          You may exercise any of these rights where indicated on our Site or by contacting us using the contact details
          provided below.
        </p>

        <p>
          We will not discriminate against you for exercising any of these rights. We may need to collect information
          from you to verify your identity, such as your email address or account information, before providing a
          substantive response to the request. In accordance with applicable laws, You may designate an authorized agent
          to make requests on your behalf to exercise your rights. Before accepting such a request from an agent, we
          will require that the agent provide proof you have authorized them to act on your behalf, and we may need you
          to verify your identity directly with us. We will respond to your request in a timely manner as required under
          applicable law.
        </p>

        <h2 v-anchor>Complaints</h2>
        <p>
          If you have complaints about how we process your personal information, please contact us using the contact
          details provided below. If you are not satisfied with our response to your complaint, depending on where you
          live you may have the right to appeal our decision by contacting us using the contact details set out below,
          or lodge your complaint with your local data protection authority.
        </p>

        <h2 v-anchor>International Users</h2>
        <p>
          Please note that we may transfer, store and process your personal information outside the country you live in,
          including the United States. Your personal information is also processed by staff and third party service
          providers and partners in these countries.
        </p>
        <p>
          If we transfer your personal information out of Europe, we will rely on recognized transfer mechanisms like
          the European Commission's Standard Contractual Clauses, or any equivalent contracts issued by the relevant
          competent authority of the UK, as relevant, unless the data transfer is to a country that has been determined
          to provide an adequate level of protection.
        </p>

        <h2 v-anchor>Contact</h2>
        <p>
          Should you have any questions about our privacy practices or this Privacy Policy, or if you would like to
          exercise any of the rights available to you, please email us at
          <ExternalLink href="mailto:support@rideparsec.com">support@rideparsec.com</ExternalLink>.
        </p>
      </div>
    </section>

    <section class="bg-primary pb-12 col-span-12 rounded-lg flex flex-col items-center">
      <div class="max-w-4xl w-full text-content">
        <h1 v-anchor>Shipping Policy</h1>

        <h2 v-anchor>Order Processing Time</h2>
        <p>Most orders are processed within 3 business days. Orders are not shipped on weekends or holidays.</p>
        <p>
          If we are experiencing a high volume of orders, shipments may be delayed by a few days. If there will be a
          significant delay in shipment of your order, we will inform you via email.
        </p>

        <h2 v-anchor>Shipping Rates and Delivery Estimates</h2>
        <p>
          Shipping and delivery time estimates are posted on individual product detail pages. Shipping rates are
          calculated at checkout.
        </p>

        <h2 v-anchor>Customs Duties</h2>
        <p>
          *This section is applicable ONLY if your order was shipped via airfreight or if your order was made in a
          country without local inventory.
        </p>
        <p>
          All customs duty is to be paid by you (the customer). Parsec will not be responsible for any fees or preparing
          documentation on your behalf. Parsec will not issue refunds for wrongfully declared imports that are in
          possession of the local customs.
        </p>

        <h2 v-anchor>Damages</h2>
        <p>
          Parsec is not liable for any products damaged or lost during shipping. For orders damaged or lost during
          shipping, get in touch with us and we will work with you to file a claim with the logistics provider. Please
          keep all packaging materials and damaged goods while a claim is in progress.
        </p>

        <h2 v-anchor>Shipping Zones</h2>
        <p>
          We are able to ship to most major markets of portable electric vehicles. If you do not see your country or
          territory listed when filling out your shipping information, please contact us and we’ll see if we can help.
        </p>
      </div>
    </section>

    <Footer />
  </main>
</template>
